import React, {useEffect, useMemo, useRef, useState} from 'react'
import {ILeague} from '@api/rank/league/league'
import MoreButton from '@feature/common/components/MoreButton/MoreButton'
import IconDown from '@svg/common/ic_btn_down.svg'
import PreviousLeagueItem from '@feature/leaderboard/pages/ScreenPreviousLeague/PreviousLeagueItem/PreviousLeagueItem'
import LeagueSideList from '@feature/leaderboard/components/LeagueSideList/LeagueSideList'
import {useRouter} from 'next/router'
import {equalString, isNotEmpty} from '@util/strings'
import useQueryRankLeague from '@hook/query/leaderboard/useQueryRankLeague'
import useWindowSize from '@hook/useWindowSize'
import LeagueBottomSheet from '@feature/leaderboard/components/LeagueBottomSheet/LeagueBottomSheet'
import useNavigation from '@hook/useNavigation'
import {useQueryGetPreviousLeagueSidebar} from '@hook/query/rank/league/useQueryGetPreviousLeagueSidebar'
import {createLog} from '@util/logs'
import SkeletonLeagueList from '@component/skeleton/feature/SkeletonLeagueList'
import useScreenLog from '@hook/useScreenLog'

const ScreenLeagueList = () => {
    const {} = useScreenLog({pageName: 'leaderboard_league_list'})

    const {replace, query, isReady} = useRouter()
    const {goLeaderboardLeague} = useNavigation()
    const {isSm} = useWindowSize()

    const [currentLeagueID, setCurrentLeagueID] = useState<string>(query.league as string)
    const [expandedItemId, setExpandedItemId] = useState('')

    const {
        data: previousLeaguePages,
        isLoading,
        isFetchingNextPage,
        fetchNextPage,
        isReachingEnd,
        isSuccess,
        isFetching,
    } = useQueryGetPreviousLeagueSidebar()

    const {data: expandedLeague, refetch, isLoading: expandedLoading} = useQueryRankLeague(expandedItemId, 'normal')

    const previousLeagues = useMemo(() => {
        const list: ILeague[] = []
        previousLeaguePages?.pages?.map(page => page?.leagues?.map(item => list?.push(item)))
        return list
    }, [previousLeaguePages])

    const onLeagueChange = (leagueID: string) => {
        setCurrentLeagueID(leagueID)
        void replace(
            {
                pathname: '/leaderboard',
                query: {tab: 'league', league: leagueID},
            },
            undefined,
            {shallow: true},
        )
    }

    const onToggleExpandItem = (id?: string) => {
        if (isNotEmpty(expandedItemId) && equalString(expandedItemId, id)) {
            setExpandedItemId('')
        } else {
            setExpandedItemId(id)
        }
    }

    const expandedRank = useMemo(() => {
        return expandedLeague?.pages?.at(0)?.slice(0, 3)
    }, [expandedLeague])

    const leagueBottomSheetRef = useRef<any>()

    useEffect(() => {
        if (isSm) {
            leagueBottomSheetRef?.current?.show()
        } else {
            leagueBottomSheetRef?.current?.close()
        }
    }, [isSm])

    const onClickLeague = e => {
        goLeaderboardLeague(e)
    }

    const onPressMoreButton = async () => {
        createLog('event', 'leaderboard_leagues_more')
        await fetchNextPage()
    }

    return (
        <div className={'grid grid-cols-[350px_auto] gap-x-[20px] lg:block'}>
            <div className={'lg:hidden'}>
                <LeagueSideList onChangedLeague={onLeagueChange} leagueId={currentLeagueID} state={'previous'} />
            </div>
            <div>
                <div className={'grid gap-y-[10px]'} data-cy={'leaderboard_table_leagueAll'}>
                    {isLoading && isFetching && !isSuccess ? (
                        <SkeletonLeagueList />
                    ) : (
                        previousLeagues?.map(league => {
                            return (
                                <PreviousLeagueItem
                                    key={league.id}
                                    onPress={() => onToggleExpandItem(league?.id)}
                                    isExpanded={equalString(league?.id, expandedItemId)}
                                    isSpinner={expandedLoading}
                                    expandedRank={expandedRank}
                                    {...league}
                                />
                            )
                        })
                    )}
                </div>

                {!isReachingEnd && !isLoading && (
                    <div className={'flex items-center min-h-[60px] justify-center mt-[10px]'}>
                        <MoreButton
                            onClick={onPressMoreButton}
                            renderRight={<IconDown width={16} className={'fill-gray01 dark:fill-dark_gray01'} />}
                            isLoading={isLoading || isFetchingNextPage}
                        />
                    </div>
                )}
            </div>
            <LeagueBottomSheet ref={leagueBottomSheetRef} onClick={onClickLeague} />
        </div>
    )
}

export default ScreenLeagueList
